"use client";

import React, { useEffect, useState } from "react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { ShieldCheck, Database, Calendar, Users, Briefcase, ClipboardList, TrendingUp, Award, Clock, MapPin } from "lucide-react";

interface Metrics {
    total_postings: number;
    active_postings: number;
    pending_review: number;
    total_assignments: number;
    completed_assignments: number;
    completed_tier1?: number;
    completed_tier2?: number;
    active_volunteers: number;
    pool_opt_ins: number;
}

interface AuditLog {
    id: string;
    action: string;
    object_type: string;
    object_id: string | null;
    scope: string | null;
    created_at: string | null;
    meta: any;
}

interface Assignment {
    id: string;
    gig_posting_id: string;
    gig_posting_title: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    volunteer_id: string;
    volunteer_name: string;
    volunteer_email: string;
    origin: string;
    status: string;
    assigned_at: string | null;
    started_at: string | null;
    completed_at: string | null;
    recognition_tier: string;
}

interface Posting {
    id: string;
    gig_type_id: string;
    gig_type_name: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    opens_at: string | null;
    closes_at: string | null;
    count_needed: number;
    filled_count: number;
    status: string;
    rejection_reason?: string | null;
    geographic_scope: string | null;
    description: string | null;
    terms_version: number;
    created_at: string | null;
}

interface GovernanceData {
    metrics: Metrics;
    recent_audits: AuditLog[];
}

interface Contributor {
    name: string;
    email: string;
    role: string;
    note: string;
    gigsCount: number;
    volunteer_id: string;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function getCategoryName(name: string): string {
    const n = name.toLowerCase();
    if (n.includes("webinar") || n.includes("rsi") || n.includes("grade") || n.includes("grading") || n.includes("exam")) return "Education";
    if (n.includes("reference") || n.includes("journal") || n.includes("cohort")) return "Membership";
    if (n.includes("challenge") || n.includes("mentor") || n.includes("alma mater")) return "Academia Outreach";
    return "Outreach";
}

const CATEGORY_STYLES: Record<string, string> = {
    "Academia Outreach": "bg-[#ffd0d6]/30 text-[#ae232d]",
    "Education":         "bg-[#cbeef2]/30 text-[#007a8a]",
    "Membership":        "bg-[#fef3c7]/60 text-[#b45309]",
    "Outreach":          "bg-slate-50 text-slate-700",
};

function getEntityStyle(type: string): string {
    const t = type.toLowerCase();
    if (t === "staff")     return "bg-rose-50 text-rose-700 border-rose-100";
    if (t === "committee") return "bg-[#003d45]/5 text-[#003d45] border-[#003d45]/10";
    if (t === "chapter")   return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/10";
    if (t === "community") return "bg-[#ae232d]/5 text-[#ae232d] border-[#ae232d]/10";
    return "bg-[#64748b]/5 text-[#64748b] border-[#64748b]/10";
}

function getEligibility(p: Posting): string {
    if (p.geographic_scope && p.geographic_scope !== "Global")
        return `Members in region (${p.geographic_scope})`;
    const n = p.gig_type_name.toLowerCase();
    if (n.includes("charterholder") || n.includes("journal") || n.includes("webinar") || n.includes("ref"))
        return "Charterholders";
    if (n.includes("candidate") || n.includes("student"))
        return "Members & candidates";
    if (n.includes("region") || n.includes("pune") || n.includes("mumbai"))
        return "Members in region";
    return "Open to all";
}

function initials(name: string): string {
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : (name[0] || "V").toUpperCase();
}

export default function GovernanceContributionTable({ onSelectVolunteer }: { onSelectVolunteer?: (id: string) => void }) {
    const [data, setData] = useState<GovernanceData | null>(null);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [postings, setPostings] = useState<Posting[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        async function fetchGovernance() {
            setIsLoading(true);
            setError(null);
            try {
                const [resGov, resAssign, resPostings] = await Promise.all([
                    fetchWithAuth(`${API_BASE}/api/v1/gigs/console/governance/contributors`),
                    fetchWithAuth(`${API_BASE}/api/v1/gigs/console/assignments?lens=governance`),
                    fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings?lens=governance`)
                ]);

                if (!resGov.ok) {
                    if (resGov.status === 403) {
                        throw new Error("Access Denied: You do not have sufficient privileges to access Governance logs.");
                    }
                    const errBody = await resGov.json().catch(() => ({}));
                    throw new Error(errBody.detail || "Failed to load governance records.");
                }

                const govResult = await resGov.json();
                let assignResult: Assignment[] = [];
                if (resAssign.ok) {
                    assignResult = await resAssign.json();
                }
                let postingsResult: Posting[] = [];
                if (resPostings.ok) {
                    postingsResult = await resPostings.json();
                }

                if (isMounted) {
                    setData(govResult);
                    setAssignments(assignResult);
                    setPostings(postingsResult);
                }
            } catch (err: any) {
                if (isMounted) {
                    setError(err.message || "An unexpected error occurred.");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        fetchGovernance();
        return () => {
            isMounted = false;
        };
    }, []);

    if (isLoading) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-3 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Assembling Governance Metrics...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="text-center py-16 bg-red-50/50 border border-red-100 rounded-xl p-6">
                <ShieldCheck className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-red-800 uppercase tracking-wider">Access Restricted</h3>
                <p className="text-xs text-red-600/80 mt-1 max-w-md mx-auto">{error || "Governance summary is not accessible."}</p>
            </div>
        );
    }

    const m = data.metrics;
    const completionRate = m.total_assignments > 0 
        ? Math.round((m.completed_assignments / m.total_assignments) * 100) 
        : 0;

    // Filter completed assignments
    const completedAssignments = assignments.filter(a => a.status.toLowerCase() === "completed");

    // Filter completed postings
    const completedPostings = postings.filter(p => p.status.toLowerCase() === "completed");

    // Group completed assignments by volunteer to compute top contributors
    const contributorMap: Record<string, Contributor> = {};
    completedAssignments.forEach(a => {
        const key = a.volunteer_email || a.volunteer_id;
        if (!contributorMap[key]) {
            contributorMap[key] = {
                name: a.volunteer_name,
                email: a.volunteer_email,
                role: a.origin === "applied" ? "Charterholder" : "Candidate",
                note: a.gig_posting_title,
                gigsCount: 0,
                volunteer_id: a.volunteer_id
            };
        } else {
            contributorMap[key].gigsCount += 1;
            if (!contributorMap[key].note.includes(a.gig_posting_title)) {
                contributorMap[key].note += " + " + a.gig_posting_title;
            }
        }
    });

    Object.values(contributorMap).forEach(c => {
        if (c.gigsCount === 0) c.gigsCount = 1;
    });

    const topContributors = Object.values(contributorMap)
        .sort((a, b) => b.gigsCount - a.gigsCount)
        .slice(0, 5);

    return (
        <div className="space-y-8 animate-in fade-in duration-200">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Postings Card */}
                <div className="bg-white rounded-xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Total Active Postings</p>
                            <h3 className="text-2xl font-black text-[#003d45] mt-1">{m.total_postings}</h3>
                        </div>
                        <div className="w-9 h-9 rounded-2xl bg-[#007a8a]/5 text-[#007a8a] flex items-center justify-center shrink-0">
                            <Briefcase className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex gap-3 mt-4 text-[10px] text-gray-400">
                        <span><strong className="text-emerald-600 font-bold">{m.active_postings}</strong> active</span>
                        <span><strong className="text-amber-500 font-bold">{m.pending_review}</strong> pending review</span>
                    </div>
                </div>

                {/* Assignments Card */}
                <div className="bg-white rounded-xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Gigs Completed</p>
                            <h3 className="text-2xl font-black text-[#003d45] mt-1">{m.total_assignments}</h3>
                        </div>
                        <div className="w-9 h-9 rounded-2xl bg-[#007a8a]/5 text-[#007a8a] flex items-center justify-center shrink-0">
                            <ClipboardList className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5 mt-3 text-[10px] text-gray-400 border-t border-gray-50 pt-3">
                        <div className="flex justify-between">
                            <span>Completed ({completionRate}% rate):</span>
                            <span className="text-emerald-600 font-bold">{m.completed_assignments}</span>
                        </div>
                        {m.completed_tier1 !== undefined && (
                            <div className="flex justify-between text-[9px]">
                                <span>Tier 1 (Core):</span>
                                <span className="font-bold text-[#003d45]">{m.completed_tier1}</span>
                            </div>
                        )}
                        {m.completed_tier2 !== undefined && (
                            <div className="flex justify-between text-[9px]">
                                <span>Tier 2 (General):</span>
                                <span className="font-bold text-[#003d45]">{m.completed_tier2}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Volunteers Card */}
                <div className="bg-white rounded-xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Engaged Volunteers</p>
                            <h3 className="text-2xl font-black text-[#003d45] mt-1">{m.active_volunteers}</h3>
                        </div>
                        <div className="w-9 h-9 rounded-2xl bg-[#007a8a]/5 text-[#007a8a] flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-4 text-[10px] text-emerald-600 font-bold">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span>Unique active contributors</span>
                    </div>
                </div>

                {/* Pools Card */}
                <div className="bg-white rounded-xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Standing Pools</p>
                            <h3 className="text-2xl font-black text-[#003d45] mt-1">{m.pool_opt_ins}</h3>
                        </div>
                        <div className="w-9 h-9 rounded-2xl bg-[#007a8a]/5 text-[#007a8a] flex items-center justify-center shrink-0">
                            <Database className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-center gap-1 mt-4 text-[10px] text-gray-400">
                        <span>Active standing availability registrations</span>
                    </div>
                </div>
            </div>

            {/* Top Contributors Section */}
            <div className="space-y-4">
                <div>
                    <h3 className="text-sm font-black text-[#003d45] uppercase tracking-wider">Top contributors</h3>
                    <p className="text-xs text-gray-400 mt-0.5">A dignified record of service — no points, no leaderboards, just who showed up</p>
                </div>

                {topContributors.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-150 p-8 shadow-sm text-center text-xs text-gray-400">
                        No completed assignments recorded to list contributors.
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden divide-y divide-gray-100">
                        {topContributors.map((c, i) => (
                            <div key={i} className="flex items-center gap-4 p-5 hover:bg-gray-50/50 transition-colors">
                                {/* Avatar */}
                                <div className="w-10 h-10 rounded-full bg-[#4b8892] text-white text-xs font-black flex items-center justify-center uppercase shrink-0">
                                    {initials(c.name)}
                                </div>
                                {/* Name and Role */}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-[#003d45]">{c.name}</div>
                                    <div className="text-xs text-gray-400 font-medium mt-0.5">{c.role} &middot; {c.email}</div>
                                </div>
                                {/* Profile Link */}
                                {onSelectVolunteer && (
                                    <button
                                        onClick={() => onSelectVolunteer(c.volunteer_id)}
                                        className="text-[#004d57] hover:text-[#003c44] font-semibold text-[11px] cursor-pointer border-none bg-transparent hover:underline uppercase tracking-wider shrink-0"
                                    >
                                        Profile
                                    </button>
                                )}
                                {/* Stat */}
                                <div className="text-center min-w-[70px] shrink-0 border-l border-gray-100 pl-4">
                                    <div className="text-xl font-black text-[#003d45]">{c.gigsCount}</div>
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mt-0.5">Gigs Done</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Top Gigs (Completed) Section */}
            <div className="space-y-4">
                <div>
                    <h3 className="text-sm font-black text-[#003d45] uppercase tracking-wider">Top Gigs</h3>
                    <p className="text-xs text-gray-400 mt-0.5">A record of successfully finished volunteer engagements and milestones</p>
                </div>

                {completedPostings.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-150 p-8 shadow-sm text-center text-xs text-gray-400">
                        No completed gigs found in this cycle.
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {completedPostings.map((p) => {
                            const categoryName = getCategoryName(p.gig_type_name);
                            const badgeStyle = CATEGORY_STYLES[categoryName] || "bg-gray-50 text-gray-700";
                            const eligibility = getEligibility(p);

                            return (
                                <div key={p.id} className="bg-white rounded-xl border border-gray-150 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-6 text-left">
                                    {/* Details */}
                                    <div className="space-y-1.5 flex-1 min-w-0">
                                        <div className="flex items-center gap-2.5 flex-wrap">
                                            <h4 className="text-base font-bold text-[#003d45]">{p.gig_type_name}</h4>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[8.5px] font-bold uppercase tracking-wider ${badgeStyle}`}>
                                                {categoryName}
                                            </span>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getEntityStyle(p.owning_entity_type)}`}>
                                                {p.owning_entity_name || `${p.owning_entity_type}: ${p.owning_entity_id}`}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-400 font-medium flex items-center flex-wrap gap-x-1.5">
                                            {p.geographic_scope && p.geographic_scope !== "Global" && <MapPin className="w-3 h-3 text-gray-400 shrink-0" />}
                                            {eligibility}
                                        </p>
                                    </div>

                                    {/* Completed Badge */}
                                    <div className="shrink-0">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border bg-[#a9c4bb]/20 text-[#004d57] border-[#a9c4bb]/40">
                                            Completed
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
